import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, Redirect } from 'react-router-dom';
import { Subscribe } from 'unstated';
import OrganizationContainer from '../../../../shared/state/containers/OrganizationContainer';
import UserContainer from '../../../../shared/state/containers/UserContainer';
import PlatformOrderingDashboardContent from './components/PlatformOrderingDashboardContent';
import PlatformSchedulingDashboardContent from './components/PlatformSchedulingDashboardContent';
import { FoodOrderingFeatureFlagId, GenAIFoodOrdering, HealthcareSchedulingFeatureFlagId, OutboundCallFeatureFlagId } from '../../../../shared/constants/featureFlagIds';
import { getApplicationsWithFeaturesForOrg } from '../../../../shared/api/controllers/applications';
import ApplicationWithMembers from '../../../../shared/models/applications/api/ApplicationWithMembers';
import { loadPlatformOrganizationApplications } from './platformOrganizationLoader';

const isFeatureAppExcluded = (app: ApplicationWithMembers) => {
    const appName = app?.name?.toLowerCase() || '';
    return appName.includes('manage menu') || appName.includes('manage entities') || appName.includes('shared topics');
};

const matchesPlatformVertical = (app: ApplicationWithMembers, vertical: 'ordering' | 'scheduling') => {
    if (isFeatureAppExcluded(app)) return false;

    const flags = app.featureFlags || [];
    if (vertical === 'ordering') {
        return flags.some(flag => flag.id === FoodOrderingFeatureFlagId || flag.id === GenAIFoodOrdering);
    }

    return flags.some(flag => flag.id === HealthcareSchedulingFeatureFlagId);
};

type PlatformRouteParams = {
    vertical: 'ordering' | 'scheduling';
};

interface PlatformDashboardOrg {
    id: string;
    name: string;
    imageUri?: string | null;
    isMultiLingual?: boolean;
    hasLiveApp: boolean;
    liveAppCount: number;
    matchingApps: ApplicationWithMembers[];
    sharedConfigurationCandidates: ApplicationWithMembers[];
}

interface PlatformDiscoveryProgress {
    loadedCount: number;
    totalCount: number;
    isComplete: boolean;
    hasErrors: boolean;
}

const buildPlatformDashboardOrganization = (
    org: any,
    applications: ApplicationWithMembers[],
    vertical: 'ordering' | 'scheduling'
): PlatformDashboardOrg | null => {
    const filteredApps = applications.filter(app => matchesPlatformVertical(app, vertical));
    if (filteredApps.length === 0) return null;

    return {
        id: org.id,
        name: org.name,
        imageUri: org.imageUri,
        isMultiLingual: org.isMultiLingual,
        hasLiveApp: filteredApps.some(app => app.isLive),
        liveAppCount: filteredApps.filter(app => app.isLive).length,
        matchingApps: filteredApps,
        sharedConfigurationCandidates: applications.filter(app => app.allowsTemplating)
    };
};

const sortPlatformOrganizations = (organizations: PlatformDashboardOrg[]): PlatformDashboardOrg[] =>
    organizations.slice().sort((first, second) => {
        if (second.liveAppCount !== first.liveAppCount) return second.liveAppCount - first.liveAppCount;
        return first.name.localeCompare(second.name);
    });

const PlatformDashboard: React.FC<RouteComponentProps<PlatformRouteParams>> = ({ match, location }) => {
    return (
        <Subscribe to={[OrganizationContainer, UserContainer]}>
            {(organizationContainer: OrganizationContainer, userContainer: UserContainer) => (
                <PlatformDashboardInner
                    vertical={match.params.vertical}
                    isManagementRoute={location.pathname.toLowerCase().endsWith('/management')}
                    organizationContainer={organizationContainer}
                    userContainer={userContainer}
                />
            )}
        </Subscribe>
    );
};

interface PlatformDashboardInnerProps {
    vertical: 'ordering' | 'scheduling';
    isManagementRoute: boolean;
    organizationContainer: OrganizationContainer;
    userContainer: UserContainer;
}

const PlatformDashboardInner: React.FC<PlatformDashboardInnerProps> = ({
    vertical,
    isManagementRoute,
    organizationContainer,
    userContainer
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isCheckingUser, setIsCheckingUser] = useState(true);
    const [hasLoadedOrganizationList, setHasLoadedOrganizationList] = useState(false);
    const [platformOrganizations, setPlatformOrganizations] = useState<PlatformDashboardOrg[]>([]);
    const [applicationLoadErrors, setApplicationLoadErrors] = useState<string[]>([]);
    const [reloadKey, setReloadKey] = useState(0);
    const [discoveryProgress, setDiscoveryProgress] = useState<PlatformDiscoveryProgress>({
        loadedCount: 0,
        totalCount: 0,
        isComplete: false,
        hasErrors: false
    });

    useEffect(() => {
        let cancelled = false;

        const ensureUser = async () => {
            try {
                if (!userContainer.state.currentUser) {
                    await userContainer.getUser();
                }
            } finally {
                if (!cancelled) {
                    setIsCheckingUser(false);
                }
            }
        };

        ensureUser();

        return () => {
            cancelled = true;
        };
    }, [userContainer]);

    const currentUserEmail = userContainer.state.currentUser?.email?.toLowerCase() || '';
    const isSystemUser = currentUserEmail.endsWith('@voicify.com');
    const canManageQueues = userContainer.state.currentUser?.canCreateOrganizations === true;

    useEffect(() => {
        let isCancelled = false;

        const load = async () => {
            if (!isSystemUser) {
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setHasLoadedOrganizationList(false);
            setPlatformOrganizations([]);
            setDiscoveryProgress({ loadedCount: 0, totalCount: 0, isComplete: false, hasErrors: false });
            setApplicationLoadErrors([]);
            try {
                const orgResult = await organizationContainer.getOrganizations();
                const orgs = orgResult?.data || organizationContainer.state.organizations || [];
                if (isCancelled) return;
                setHasLoadedOrganizationList(true);

                setDiscoveryProgress({
                    loadedCount: 0,
                    totalCount: orgs.length,
                    isComplete: orgs.length === 0,
                    hasErrors: false
                });
                let discoveryHadErrors = false;
                const organizationApplicationResults = await loadPlatformOrganizationApplications(
                    orgs,
                    getApplicationsWithFeaturesForOrg,
                    6,
                    (result, loadedCount, totalCount) => {
                        if (isCancelled) return;
                        discoveryHadErrors = discoveryHadErrors || Boolean(result.error);
                        if (loadedCount % 5 === 0 || loadedCount === totalCount) {
                            setDiscoveryProgress({
                                loadedCount,
                                totalCount,
                                isComplete: loadedCount === totalCount,
                                hasErrors: discoveryHadErrors
                            });
                        }

                        if (canManageQueues && !result.error) {
                            const organization = buildPlatformDashboardOrganization(
                                result.organization,
                                result.applications as ApplicationWithMembers[],
                                vertical
                            );
                            if (organization) {
                                setPlatformOrganizations(previous => sortPlatformOrganizations([
                                    ...previous.filter(item => item.id !== organization.id),
                                    organization
                                ]));
                            }
                        }
                    }
                );
                const matchingOrgs = organizationApplicationResults.map(({ organization: org, applications, error }) => {
                    if (error) return null;
                    return buildPlatformDashboardOrganization(org, applications as ApplicationWithMembers[], vertical);
                });

                if (!isCancelled) {
                    const failedResults = organizationApplicationResults.filter(result => Boolean(result.error));
                    setApplicationLoadErrors(failedResults
                        .map(result => `${result.organization.name}: applications could not be loaded after 3 attempts. ${result.error}`));
                    const platformOrgs = sortPlatformOrganizations(
                        matchingOrgs.filter((org): org is PlatformDashboardOrg => Boolean(org))
                    );

                    setPlatformOrganizations(platformOrgs);
                    setDiscoveryProgress({
                        loadedCount: orgs.length,
                        totalCount: orgs.length,
                        isComplete: true,
                        hasErrors: failedResults.length > 0
                    });
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        };

        if (!isCheckingUser) {
            load();
        }

        return () => {
            isCancelled = true;
        };
    }, [vertical, isSystemUser, isCheckingUser, canManageQueues, organizationContainer, reloadKey]);

    const analyticsOrganizations = useMemo(
        () => platformOrganizations.filter(organization => organization.hasLiveApp),
        [platformOrganizations]
    );

    const entityNameMap = useMemo(() => {
        return analyticsOrganizations.reduce((acc, org) => {
            acc[org.id] = org.name;
            return acc;
        }, {} as Record<string, string>);
    }, [analyticsOrganizations]);

    const appNameMap = useMemo(() => {
        return analyticsOrganizations.reduce((acc, org) => {
            org.matchingApps.forEach(app => {
                acc[app.id] = `${org.name} - ${app.name}`;
            });
            return acc;
        }, {} as Record<string, string>);
    }, [analyticsOrganizations]);

    const representativeAppId = useMemo(() => {
        return analyticsOrganizations[0]?.matchingApps?.[0]?.id;
    }, [analyticsOrganizations]);

    const appIdsByOrganizationId = useMemo(() => {
        return analyticsOrganizations.reduce((acc, org) => {
            acc[org.id] = org.matchingApps.map(app => app.id);
            return acc;
        }, {} as Record<string, string[]>);
    }, [analyticsOrganizations]);

    const appLiveStatusMap = useMemo(() => {
        return analyticsOrganizations.reduce((acc, org) => {
            org.matchingApps.forEach(app => {
                acc[app.id] = app.isLive === true;
            });
            return acc;
        }, {} as Record<string, boolean>);
    }, [analyticsOrganizations]);

    const hasOutboundCallApps = useMemo(() => {
        return analyticsOrganizations.some(org =>
            org.matchingApps.some(app => app.featureFlags?.some(flag => flag.id === OutboundCallFeatureFlagId))
        );
    }, [analyticsOrganizations]);

    const managementOrganizationItems = useMemo(() => {
        return platformOrganizations.map(org => ({
            id: org.id,
            name: org.name,
            imageUri: org.imageUri,
            isMultiLingual: org.isMultiLingual,
            hasLiveApp: org.hasLiveApp,
            liveAppCount: org.liveAppCount,
            matchingAppCount: org.matchingApps.length,
            matchingAppIds: org.matchingApps.map(app => app.id),
            matchingAppLiveStatusMap: org.matchingApps.reduce((acc, app) => {
                acc[app.id] = app.isLive === true;
                return acc;
            }, {} as Record<string, boolean>),
            matchingApps: org.matchingApps.map(app => ({
                id: app.id,
                name: app.name,
                isLive: app.isLive === true
            })),
            sharedConfigurationCandidates: org.sharedConfigurationCandidates.map(app => ({
                id: app.id,
                name: app.name,
                isLive: app.isLive === true
            }))
        }));
    }, [platformOrganizations]);

    const analyticsOrganizationItems = useMemo(
        () => managementOrganizationItems.filter(organization => organization.hasLiveApp),
        [managementOrganizationItems]
    );

    const isViewLoading = isManagementRoute ? !hasLoadedOrganizationList : isLoading;

    if (isCheckingUser) {
        return null;
    }

    if (!isSystemUser) {
        return <Redirect to="/v" />;
    }

    if (vertical === 'ordering') {
        return (
            <PlatformOrderingDashboardContent
                organizations={analyticsOrganizationItems}
                managementOrganizations={managementOrganizationItems}
                appNameMap={appNameMap}
                entityNameMap={entityNameMap}
                representativeAppId={representativeAppId}
                platformAppIdsByOrganizationId={appIdsByOrganizationId}
                selectedAppLiveStatusMap={appLiveStatusMap}
                canManageQueues={canManageQueues}
                loadErrors={applicationLoadErrors}
                discoveryProgress={discoveryProgress}
                onReload={() => setReloadKey(previous => previous + 1)}
                isLoading={isViewLoading}
            />
        );
    }

    return (
        <PlatformSchedulingDashboardContent
            organizations={analyticsOrganizationItems}
            managementOrganizations={managementOrganizationItems}
            appNameMap={appNameMap}
            entityNameMap={entityNameMap}
            representativeAppId={representativeAppId}
            platformAppIdsByOrganizationId={appIdsByOrganizationId}
            selectedAppLiveStatusMap={appLiveStatusMap}
            hasOutboundCallApps={hasOutboundCallApps}
            canManageQueues={canManageQueues}
            loadErrors={applicationLoadErrors}
            discoveryProgress={discoveryProgress}
            onReload={() => setReloadKey(previous => previous + 1)}
            isLoading={isViewLoading}
        />
    );
};

export default PlatformDashboard;
